import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, FileTextIcon, UserPen, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppTour from "../../../../components/AppTour/AppTour";
import MusicLoader from "../../../../helper/MusicLoader";
import { gettingStartedTourSteps } from "../../../../components/AppTour/ModuleTourSteps";
import { useContents } from "../../../../queries/professoru/contents.api";
import { getYoutubeThumbnail } from "../../../../helper/utility";
import { ReactComponent as GrowthEngineIcon } from "../../../../assets/images/icons/marketing-manager.svg";
import { ReactComponent as PublicistIcon } from "../../../../assets/images/icons/publicist.svg";
import { ReactComponent as AudioIcon } from "../../../../assets/images/icons/audio.svg";
import { ReactComponent as AnalystIcon } from "../../../../assets/images/icons/analyst.svg";
import { ReactComponent as AmplitudeIcon } from "../../../../assets/images/icons/button-icon.svg";
import { ReactComponent as ArtistIcon } from "../../../../assets/images/icons/artist.svg";
import { ReactComponent as ProfessorUIcon } from "../../../../assets/images/icons/professor.svg";
import { ReactComponent as CreatorIcon } from "../../../../assets/images/icons/creator.svg";
import { ReactComponent as DistributionIcon } from "../../../../assets/images/icons/distribution-white.svg";

const moduleIconMap = {
  "Growth Engine": GrowthEngineIcon,
  "Publicist": PublicistIcon,
  "UrStudio": AudioIcon,
  "Audio Engineer": AudioIcon,
  "Data Analyst": AnalystIcon,
  "Amplitude AI": AmplitudeIcon,
  "Artist Manager": ArtistIcon,
  "Artsist Manager": ArtistIcon,
  "ProfessorU": ProfessorUIcon,
  "Content Creator": CreatorIcon,
  "Distribution": DistributionIcon,
  "Account": UserPen,
  "Teams": Users,
};

const getSecondTag = (item) => {
  const tags = Array.isArray(item?.tags) ? item.tags : [];
  return tags[1] || "";
};

const formatTagLabel = (tag = "") =>
  String(tag)
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

function Thumbnail({ item, type }) {
  if (item.type === "icon") {
    const Icon = item.thumbnail;
    return (
      <Icon className="w-[160px] h-[160px] text-theme_clr1" />
    );
  }

  const mediaUrl = item.content?.mediaUrl;
  const thumbnail = type === "interview" && mediaUrl && /youtube|youtu\.be/.test(mediaUrl)
    ? getYoutubeThumbnail(mediaUrl)
    : (item.thumbnail?.url || item.thumbnail?.externalUrl || item.thumbnail);

  if (!thumbnail) {
    return (
      <FileTextIcon className="w-[160px] h-[160px] text-theme_clr1" />
    )
  }
  return (
    <img src={thumbnail} alt={item.title} className="w-[250px] h-[200px] object-cover" />
  );
}

export default function InterviewsAndGuides({ title, type = "guide" }) {
  const scrollRef = useRef(null);
  const navigate = useNavigate()

  const [page] = useState(1);
  const [runTour, setRunTour] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { data: contentsResponse, isPending, isFetching } = useContents({ type, page, limit: 100 });
  const lessonsData = contentsResponse?.data || [];

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollButtons();
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => updateScrollButtons();
    const handleResize = () => updateScrollButtons();

    el.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [lessonsData, isPending, isFetching]);

  const handleLessonClick = (item, index) => {
    if (title === "Learn by Module") {
      navigate(`/admin/professor-u/lessons?lesson=${item.title}&moduleId=${item.id}`);
      return;
    }
    if (title === "Lessons with ProfessorU" && index === 0) {
      setRunTour(true);
      return;
    }
    if (title === "Lessons with ProfessorU") {
      navigate(item.url + '#start-tour');
      return;
    }
    navigate(`/admin/professor-u/guides?content=${encodeURIComponent(title)}&type=${encodeURIComponent(type)}&moduleId=${item.moduleId}&contentId=${item._id}`);
  };

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;

    const card = el.querySelector(".snap-start");
    if (!card) return;

    const cardWidth = card.offsetWidth + 16; // 16 = gap-4

    el.scrollBy({
      left: dir === "left" ? -cardWidth : cardWidth,
      behavior: "smooth",
    });
  };

  return (
    <div className="w-full bg-theme_clr1 rounded-2xl p-4 mt-4 relative">
      <AppTour
        tourSteps={gettingStartedTourSteps}
        runTour={runTour}
        setRunTour={setRunTour}
        goBack={false}
        tourName={`professoru-${type}-carousel`}
        moduleId="professoru"
      />
      {/* Header */}
      <div className="flex items-center gap-2 text-white mb-6">
        <div className="title-info text-[23px] font-bold font-manrope flex items-center gap-2 leading-tight">
          <img src={require("../../../../assets/images/admin/Amplitude-Info.png")} className="inline w-[30px] h-[30px]" />
          {title}
        </div>
      </div>

      {/* Carousel Row */}
      <div className="flex items-center gap-4">

        {/* LEFT ARROW */}
        <button
          onClick={() => scroll("left")}
          disabled={!canScrollLeft || isFetching}
          className={`flex-shrink-0 transition
            ${canScrollLeft ? "" : "opacity-40 cursor-not-allowed"}
          `}
        >
          <ChevronLeft className="text-white" />
        </button>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto flex-1 no-scrollbar snap-x snap-mandatory px-2 py-2"
        >
          {(isPending || isFetching) && (
            <div className="flex items-center justify-center w-full min-h-[250px]">
              <MusicLoader />
            </div>
          )}
          {!isFetching && lessonsData.map((item, index) => {
            const secondTag = type === "troubleshooting" ? getSecondTag(item) : "";
            const badgeLabel = type === "troubleshooting"
              ? formatTagLabel(secondTag)
              : (item.moduleName === "Artsist Manager" ? "Artist Manager" : item.moduleName);
            const ModuleIcon = moduleIconMap[secondTag]
              || moduleIconMap[formatTagLabel(secondTag)]
              || moduleIconMap[item.moduleName]
              || (type === "troubleshooting" ? FileTextIcon : null);
            const isLucideIcon = ModuleIcon === UserPen || ModuleIcon === Users || ModuleIcon === FileTextIcon;
            return (
              <div
                key={item._id}
                onClick={() => handleLessonClick(item, index)}
                className="flex-shrink-0 snap-start w-[242px] min-h-[250px] bg-gray-200 rounded-xl flex flex-col items-center p-3 transition-all duration-300 hover:-translate-y-1 hover:scale-[1.03] hover:shadow-lg cursor-pointer overflow-visible"
              >
                {((type === "guide" && item.moduleName) || (type === "troubleshooting" && secondTag)) && (
                  <div className="w-full flex items-center justify-center gap-1 text-xs font-semibold text-white bg-theme_clr1 rounded-md px-2 py-1 mb-2">
                    {ModuleIcon && (
                      <ModuleIcon className={`w-4 h-4 flex-shrink-0 ${isLucideIcon ? "text-white" : "fill-white"}`} />
                    )}
                    <span className="truncate">
                      {badgeLabel}
                    </span>
                  </div>
                )}
                <div className="flex-1 flex items-center justify-center overflow-hidden w-full rounded-lg">
                  <Thumbnail item={item} type={type} />
                </div>

                <p
                  title={item.title}
                  className="w-full min-w-0 text-center text-sm font-normal text-theme_clr1 mt-2 leading-tight px-1 break-words whitespace-normal"
                >
                  {index + 1}. {type === "troubleshooting" && secondTag ? `${formatTagLabel(secondTag)}: ` : ""}{item?.title}
                </p>
              </div>
            );
          })}
        </div>

        {/* RIGHT ARROW */}
        <button
          onClick={() => scroll("right")}
          disabled={!canScrollRight || isFetching}
          className={`flex-shrink-0 transition
            ${canScrollRight ? "" : "opacity-40 cursor-not-allowed"}
          `}
        >
          <ChevronRight className="text-white" />
        </button>
      </div>
    </div>
  );
}